import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';
import { toast } from 'react-toastify';
import supplierService, {
  computeInvoiceTotals,
  InvoiceLineProduct,
  PurchaseInvoice,
} from '../../services/supplierService';
import { toPersianDigitsStr } from '../../utils/persianNumber';
import { faNum, faPercent, faRial, faToman, invoiceDateLabel, marginOf, storeName, supplierName, WEB_BASE } from './format';

interface Props {
  invoiceId: string | null;
  onClose: () => void;
}

const Stat: React.FC<{ label: string; value: React.ReactNode; color?: string }> = ({ label, value, color }) => (
  <Box sx={{ minWidth: 140 }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography fontWeight={700} sx={{ color }}>
      {value}
    </Typography>
  </Box>
);

const lineProduct = (p: unknown): InvoiceLineProduct | null =>
  p && typeof p === 'object' && (p as any)._id ? (p as InvoiceLineProduct) : null;

const InvoiceDetailDrawer: React.FC<Props> = ({ invoiceId, onClose }) => {
  const [invoice, setInvoice] = useState<PurchaseInvoice | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!invoiceId) {
      setInvoice(null);
      return;
    }
    let alive = true;
    setLoading(true);
    supplierService
      .getInvoice(invoiceId)
      .then((inv) => alive && setInvoice(inv))
      .catch((e: any) => {
        toast.error(e?.message || 'خطا در دریافت فاکتور');
        if (alive) setInvoice(null);
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [invoiceId]);

  const totals = useMemo(() => {
    if (!invoice) return null;
    const t = invoice.totals && invoice.totals.quantity ? invoice.totals : computeInvoiceTotals(invoice.lines || []);
    const profit = t.saleToman - t.costToman;
    return { ...t, profit, margin: marginOf(t.costToman, t.saleToman) };
  }, [invoice]);

  return (
    <Drawer
      anchor="left"
      open={!!invoiceId}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', md: '82vw', lg: 1150 }, maxWidth: '100%' } }}
    >
      <Box dir="rtl" sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ flex: 1 }}>
            فاکتور خرید {invoice ? `شماره ${toPersianDigitsStr(invoice.invoiceNumber)}` : ''}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {loading || !invoice || !totals ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            {loading ? <CircularProgress /> : <Typography color="text.secondary">فاکتوری انتخاب نشده</Typography>}
          </Box>
        ) : (
          <>
            <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              <Stat label="تأمین‌کننده" value={supplierName(invoice.supplier)} />
              <Stat label="تاریخ فاکتور" value={invoiceDateLabel(invoice)} />
              <Stat label="فروشگاه" value={storeName(invoice.store)} />
              <Stat label="مبلغ پرداختی" value={faRial(invoice.paidAmountRial ?? 0)} />
              {invoice.discountDescription && <Stat label="تخفیف" value={invoice.discountDescription} />}
              {invoice.source && (
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    منبع
                  </Typography>
                  <Chip size="small" label={invoice.source} variant="outlined" />
                </Box>
              )}
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', flexWrap: 'wrap', gap: 3, bgcolor: 'action.hover' }}>
              <Stat label="تعداد اقلام" value={faNum(totals.quantity)} />
              <Stat label="جمع قیمت پایه" value={faToman(totals.baseToman)} />
              <Stat label="جمع بهای تمام‌شده" value={faToman(totals.costToman)} />
              <Stat label="جمع فروش بالقوه" value={faToman(totals.saleToman)} />
              <Stat
                label="سود بالقوه"
                value={faToman(totals.profit)}
                color={totals.profit >= 0 ? 'success.main' : 'error.main'}
              />
              <Stat label="حاشیه سود (از فروش)" value={faPercent(totals.margin)} />
            </Paper>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>محصول</TableCell>
                    <TableCell>کد کالا</TableCell>
                    <TableCell>برند</TableCell>
                    <TableCell align="center">تعداد</TableCell>
                    <TableCell>بهای واحد</TableCell>
                    <TableCell>فروش واحد</TableCell>
                    <TableCell>جمع بهای تمام‌شده</TableCell>
                    <TableCell>جمع فروش</TableCell>
                    <TableCell>سود</TableCell>
                    <TableCell align="center">حاشیه</TableCell>
                    <TableCell align="center">موجودی فعلی</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(invoice.lines || []).map((l, i) => {
                    const p = lineProduct(l.product);
                    const qty = Number(l.quantity) || 0;
                    const cost = (Number(l.unitCostToman) || 0) * qty;
                    const sale = (Number(l.unitSaleToman) || 0) * qty;
                    const profit = sale - cost;
                    const href = p ? `${WEB_BASE}/products/${p.slug || p._id}` : null;
                    return (
                      <React.Fragment key={l._id || i}>
                        <TableRow hover sx={l.note ? { '& td': { borderBottom: 0 } } : undefined}>
                          <TableCell>{faNum(i + 1)}</TableCell>
                          <TableCell sx={{ minWidth: 240 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                              <Avatar
                                variant="rounded"
                                src={p?.thumbnail || undefined}
                                alt={l.name}
                                sx={{ width: 44, height: 44, bgcolor: 'grey.100' }}
                              >
                                <ImageNotSupportedIcon fontSize="small" color="disabled" />
                              </Avatar>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" fontWeight={600}>
                                  {p?.name || l.name}
                                </Typography>
                                {href ? (
                                  <Link href={href} target="_blank" rel="noreferrer" variant="caption" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                                    مشاهده در سایت <OpenInNewIcon sx={{ fontSize: 12 }} />
                                  </Link>
                                ) : (
                                  <Typography variant="caption" color="warning.main">
                                    به محصول متصل نیست
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell dir="ltr">{l.productCode ? toPersianDigitsStr(l.productCode) : '—'}</TableCell>
                          <TableCell>{l.brand || '—'}</TableCell>
                          <TableCell align="center">{faNum(qty)}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {faToman(l.unitCostToman)}
                            {l.unitBasePriceRial ? (
                              <Tooltip title="قیمت پایه فاکتور">
                                <Typography variant="caption" display="block" color="text.secondary">
                                  پایه: {faRial(l.unitBasePriceRial)}
                                </Typography>
                              </Tooltip>
                            ) : null}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{faToman(l.unitSaleToman)}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{faToman(cost)}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{faToman(sale)}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap', color: profit >= 0 ? 'success.main' : 'error.main' }}>
                            {faToman(profit)}
                          </TableCell>
                          <TableCell align="center">{faPercent(marginOf(cost, sale))}</TableCell>
                          <TableCell align="center">{p && p.stock !== undefined ? faNum(p.stock) : '—'}</TableCell>
                        </TableRow>
                        {l.note && (
                          <TableRow>
                            <TableCell />
                            <TableCell colSpan={11} sx={{ pt: 0 }}>
                              <Typography variant="caption" color="text.secondary">
                                یادداشت داخلی: {l.note}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                  <TableRow sx={{ '& td': { fontWeight: 700, bgcolor: 'action.hover' } }}>
                    <TableCell colSpan={4}>جمع</TableCell>
                    <TableCell align="center">{faNum(totals.quantity)}</TableCell>
                    <TableCell colSpan={2} />
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{faToman(totals.costToman)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{faToman(totals.saleToman)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{faToman(totals.profit)}</TableCell>
                    <TableCell align="center">{faPercent(totals.margin)}</TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            {invoice.notes && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  یادداشت فاکتور
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                  {invoice.notes}
                </Typography>
              </>
            )}
          </>
        )}
      </Box>
    </Drawer>
  );
};

export default InvoiceDetailDrawer;
